import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import { useTheme } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import aiAutomationApi from '../utils/aiAutomationApi';
import { formatTime12hr } from '../utils/timeFormat';
import { getCid, getMetadata } from '../utils/encryptedStorage';
import { handleMailProviderReconnect, isMailProviderAuthError } from '../utils/gmailOAuth';

// ── Status badge config ──────────────────────────────────────────────────────
const STATUS = {
  pending:             { label: 'Pending',      bg: '#FFFBEB', color: '#92400E', ring: '#FCD34D', dot: '#F59E0B' },
  needs_manual_action: { label: 'Needs Action', bg: '#EFF6FF', color: '#1E40AF', ring: '#93C5FD', dot: '#3B82F6' },
  approved:            { label: 'Approved',     bg: '#F0FDF4', color: '#166534', ring: '#86EFAC', dot: '#22C55E' },
  rejected:            { label: 'Rejected',     bg: '#FFF1F2', color: '#9F1239', ring: '#FDA4AF', dot: '#EF4444' },
};

const RefreshIcon = ({ spinning }) => (
  <svg className={`w-4 h-4 ${spinning ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
  </svg>
);

const CalendarIcon = ({ size = 4 }) => (
  <svg className={`${{ 3: 'w-3 h-3', 4: 'w-4 h-4', 5: 'w-5 h-5', 8: 'w-8 h-8' }[size] || 'w-4 h-4'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <rect x="3" y="4" width="18" height="18" rx="2" strokeWidth="2"/>
    <path strokeLinecap="round" strokeWidth="2" d="M16 2v4M8 2v4M3 10h18"/>
  </svg>
);

const UserIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
  </svg>
);

const MailIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <rect x="6" y="2" width="12" height="20" rx="2" strokeWidth="2"/>
    <path strokeLinecap="round" strokeWidth="2" d="M11 18h2"/>
  </svg>
);

const BriefcaseIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <rect x="2" y="7" width="20" height="14" rx="2" strokeWidth="2"/>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/>
  </svg>
);

const FilterIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/>
  </svg>
);

const ClearIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
  </svg>
);

const BellIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2a2 2 0 01-.6 1.4L4 17h5m6 0a3 3 0 11-6 0"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
  </svg>
);

const XCircleIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
  </svg>
);

const ListIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>
  </svg>
);

const MessageIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a8 8 0 01-8 8H7l-4 3 1.4-5.2A8 8 0 1121 12z"/>
  </svg>
);

const InfoTile = ({ icon, label, children, isDark }) => (
  <div className={`rounded-xl border p-3 ${isDark ? 'border-gray-800 bg-gray-800/55' : 'border-gray-100 bg-slate-50'}`}>
    <div className={`mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
      {icon}
      <span>{label}</span>
    </div>
    <div className={`text-xs leading-5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
      {children}
    </div>
  </div>
);

const PAGE_SIZE = 10;

// ── Component ────────────────────────────────────────────────────────────────
const RescheduleRequestsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark } = useTheme();
  const { settings } = useSettings();
  const primaryColor = settings?.primaryColor || '#3B5BDB';
  const metadata = getMetadata();
  const companyId = String(metadata?.cid || '');
  const scopedSearchId = searchParams.get('search_id') || '';
  const scopedSessionId = searchParams.get('session_id') || '';

  const [requests, setRequests] = useState([]);
  const [totalRequests, setTotalRequests] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Status tab filter – 'all' | 'active' | 'approved' | 'rejected'
  const [statusTab, setStatusTab] = useState('active');

  // Filters
  const [nameFilter, setNameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [jobFilter, setJobFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Approve modal
  const [approvingReq, setApprovingReq] = useState(null);
  const [approveDate, setApproveDate] = useState('');
  const [approveTime, setApproveTime] = useState('');
  const [approveError, setApproveError] = useState('');
  const [approveLoading, setApproveLoading] = useState(false);

  // Reject modal
  const [rejectingReq, setRejectingReq] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  const activeFilterCount = useMemo(
    () => [nameFilter, emailFilter, jobFilter].filter(Boolean).length,
    [nameFilter, emailFilter, jobFilter],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [statusTab, nameFilter, emailFilter, jobFilter, companyId, scopedSearchId, scopedSessionId]);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      // Apply status filter based on active tab
      if (statusTab === 'active') {
        params.append('status', 'pending');
        params.append('status', 'needs_manual_action');
      } else if (statusTab === 'approved') {
        params.set('status', 'approved');
      } else if (statusTab === 'rejected') {
        params.set('status', 'rejected');
      }
      // 'all' → no status param → backend returns everything
      if (companyId) params.set('cid', companyId);
      if (scopedSearchId) params.set('search_id', scopedSearchId);
      if (scopedSessionId) params.set('session_id', scopedSessionId);
      if (nameFilter)  params.set('name', nameFilter);
      if (emailFilter) params.set('email', emailFilter);
      if (jobFilter)   params.set('job_title', jobFilter);
      params.set('paginated', 'true');
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String((currentPage - 1) * PAGE_SIZE));
      const data = await aiAutomationApi.get(`/interviews/reschedule-requests?${params.toString()}`);
      if (Array.isArray(data)) {
        setRequests(data);
        setTotalRequests(data.length);
      } else {
        setRequests(Array.isArray(data?.requests) ? data.requests : []);
        setTotalRequests(Number(data?.total || 0));
      }
    } catch {
      setError('Failed to load reschedule requests. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [statusTab, nameFilter, emailFilter, jobFilter, companyId, scopedSearchId, scopedSessionId, currentPage]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const pendingCount = requests.filter(
    (r) => r.status === 'pending' || r.status === 'needs_manual_action',
  ).length;

  const clearAllFilters = () => {
    setNameFilter('');
    setEmailFilter('');
    setJobFilter('');
  };

  // ── Approve ──────────────────────────────────────────────────────────────
  const openApprove = (req) => {
    const dates = req.requested_dates?.length ? req.requested_dates
      : req.requested_date ? [req.requested_date] : [];
    setApprovingReq(req);
    setApproveDate(dates[0] || '');
    setApproveTime('');
    setApproveError('');
  };

  const handleApprove = async () => {
    if (!approveDate || !approveTime) {
      setApproveError('Please select both a date and a time.');
      return;
    }
    setApproveLoading(true);
    setApproveError('');
    try {
      const result = await aiAutomationApi.post(
        `/interviews/reschedule-requests/${approvingReq.id}/approve`,
        { scheduled_date: approveDate, scheduled_time: approveTime },
      );
      if (result?.gmail_auth_needed || result?.mail_auth_needed || isMailProviderAuthError(result)) {
        const interviewId = result?.interview_id || result?.interview?.id || approvingReq?.interview_id;
        setApproveError('Reschedule approved. Mail reconnect is required to send the confirmation email.');
        await handleMailProviderReconnect({
          cid: getCid(),
          error: result,
          onConnected: async () => {
            if (!interviewId) {
              setApproveError('Mail connected, but confirmation email could not be retried automatically.');
              return;
            }
            try {
              const resend = await aiAutomationApi.post(`/interviews/${interviewId}/resend-confirmation`, {});
              if (resend?.success) {
                setApproveError('');
              } else if (resend?.gmail_auth_needed || resend?.mail_auth_needed || isMailProviderAuthError(resend)) {
                setApproveError('Mail provider is still not connected. Please reconnect and try again.');
              } else {
                setApproveError('Could not send the confirmation email. Please try resending.');
              }
            } catch (err) {
              setApproveError(err?.message || 'Could not send the confirmation email. Please try resending.');
            }
          },
          onError: (msg) => setApproveError(msg || 'Mail reconnect was cancelled or failed.'),
        });
      }
      setApprovingReq(null);
      fetchRequests();
    } catch (err) {
      setApproveError(err?.message || 'Failed to approve. Please try again.');
    } finally {
      setApproveLoading(false);
    }
  };

  // ── Reject ───────────────────────────────────────────────────────────────
  const handleReject = async () => {
    setRejectLoading(true);
    try {
      await aiAutomationApi.post(
        `/interviews/reschedule-requests/${rejectingReq.id}/reject`,
        { decline_reason: declineReason },
      );
      setRejectingReq(null);
      setDeclineReason('');
      fetchRequests();
    } catch {
      setRejectingReq(null);
    } finally {
      setRejectLoading(false);
    }
  };

  const preferredDates = approvingReq
    ? (approvingReq.requested_dates?.length
        ? approvingReq.requested_dates
        : approvingReq.requested_date ? [approvingReq.requested_date] : [])
    : [];
  const totalPages = Math.max(1, Math.ceil(totalRequests / PAGE_SIZE));
  const pageStart = totalRequests === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(currentPage * PAGE_SIZE, totalRequests);
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-950' : 'bg-slate-50'}`}>
      <AppHeader />

      <div className="w-full px-3 py-5 sm:px-4 lg:px-5">

        {/* ── Page header ─────────────────────────────────────────────── */}
        <div className={`rounded-2xl p-5 sm:p-6 mb-5 border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'} shadow-sm`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className={`p-2 rounded-xl border transition-all hover:shadow-sm ${isDark ? 'border-gray-700 bg-gray-800 text-gray-400 hover:text-white hover:border-gray-600' : 'border-gray-200 bg-gray-50 text-gray-500 hover:text-gray-900 hover:border-gray-300'}`}
                title="Go back"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                </svg>
              </button>

              <div className="flex items-center gap-3">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center shadow-sm"
                  style={{ background: `linear-gradient(135deg, ${primaryColor}22, ${primaryColor}10)`, border: `1px solid ${primaryColor}25` }}
                >
                  <CalendarIcon size={5} />
                </div>
                <div>
                  <div className="flex items-center gap-2.5">
                    <h1 className={`text-xl font-bold leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Reschedule Requests
                    </h1>
                    {pendingCount > 0 && (
                      <span
                        className="inline-flex items-center justify-center h-6 min-w-[24px] px-2 rounded-full text-xs font-bold text-white shadow-sm animate-pulse"
                        style={{ backgroundColor: '#EF4444' }}
                      >
                        {pendingCount}
                      </span>
                    )}
                  </div>
                  <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Review and action candidate reschedule requests
                  </p>
                  {(scopedSearchId || scopedSessionId) && (
                    <div className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-slate-100 text-slate-600'}`}>
                      <BriefcaseIcon />
                      Current interview workspace
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 self-end sm:self-auto">
              {/* Filter toggle */}
              <button
                onClick={() => setShowFilters((v) => !v)}
                className={`relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                  showFilters
                    ? 'text-white shadow-md'
                    : isDark
                      ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                }`}
                style={showFilters ? { backgroundColor: primaryColor, borderColor: primaryColor } : {}}
              >
                <FilterIcon />
                Filters
                {activeFilterCount > 0 && (
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold bg-white text-gray-900">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {/* Refresh */}
              <button
                onClick={fetchRequests}
                disabled={loading}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all hover:shadow-sm ${
                  isDark
                    ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700 disabled:opacity-40'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40'
                }`}
              >
                <RefreshIcon spinning={loading} />
                Refresh
              </button>
            </div>
          </div>

          {/* ── Filters panel (collapsible) ───────────────────────────── */}
          {showFilters && (
            <div className={`mt-5 pt-5 border-t ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
              <div className="flex items-center justify-between mb-3">
                <p className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Search Filters
                </p>
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearAllFilters}
                    className="flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-600 transition-colors"
                  >
                    <ClearIcon />
                    Clear all
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { value: nameFilter,  setter: setNameFilter,  placeholder: 'Candidate name…', icon: <UserIcon /> },
                  { value: emailFilter, setter: setEmailFilter, placeholder: 'Email address…',  icon: <MailIcon /> },
                  { value: jobFilter,   setter: setJobFilter,   placeholder: 'Job title…',      icon: <BriefcaseIcon /> },
                ].map(({ value, setter, placeholder, icon }) => (
                  <div key={placeholder} className="relative group">
                    <div className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'} group-focus-within:text-blue-500 transition-colors`}>
                      {icon}
                    </div>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => setter(e.target.value)}
                      placeholder={placeholder}
                      className={`w-full pl-9 pr-9 py-2.5 rounded-xl border text-sm transition-all outline-none ${
                        isDark
                          ? 'bg-gray-800 border-gray-700 text-gray-200 placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30'
                          : 'bg-gray-50 border-gray-200 text-gray-700 placeholder-gray-400 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30'
                      }`}
                    />
                    {value && (
                      <button
                        onClick={() => setter('')}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-md transition-colors ${isDark ? 'text-gray-500 hover:text-gray-300 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'}`}
                      >
                        <ClearIcon />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {activeFilterCount > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Active:</span>
                  {nameFilter && (
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-blue-50 text-blue-700'}`}>
                      Name: {nameFilter}
                      <button onClick={() => setNameFilter('')} className="hover:opacity-70"><ClearIcon /></button>
                    </span>
                  )}
                  {emailFilter && (
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-blue-50 text-blue-700'}`}>
                      Email: {emailFilter}
                      <button onClick={() => setEmailFilter('')} className="hover:opacity-70"><ClearIcon /></button>
                    </span>
                  )}
                  {jobFilter && (
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-blue-50 text-blue-700'}`}>
                      Job: {jobFilter}
                      <button onClick={() => setJobFilter('')} className="hover:opacity-70"><ClearIcon /></button>
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Status tabs ────────────────────────────────────────────── */}
        <div className={`mb-5 overflow-x-auto rounded-2xl border p-1.5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'} shadow-sm`}>
          <div className="inline-flex min-w-full gap-1 sm:min-w-0">
          {[
            { key: 'active',   label: 'Active',   icon: <BellIcon /> },
            { key: 'approved', label: 'Approved', icon: <CheckCircleIcon /> },
            { key: 'rejected', label: 'Rejected', icon: <XCircleIcon /> },
            { key: 'all',      label: 'All',      icon: <ListIcon /> },
          ].map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => setStatusTab(key)}
              className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap px-4 py-2 rounded-xl text-sm font-medium transition-all sm:flex-none ${
                statusTab === key
                  ? 'text-white shadow-sm'
                  : isDark
                    ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              style={statusTab === key ? { backgroundColor: primaryColor } : {}}
            >
              {icon}
              {label}
            </button>
          ))}
          </div>
        </div>

        {/* ── Summary stats bar ────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-3 mb-5 sm:grid-cols-5">
          {[
            { label: 'Total', value: totalRequests, color: primaryColor, bgLight: `${primaryColor}08`, bgDark: `${primaryColor}15` },
            { label: 'Pending', value: requests.filter(r => r.status === 'pending').length, color: '#F59E0B', bgLight: '#FFFBEB', bgDark: '#78350F20' },
            { label: 'Needs Action', value: requests.filter(r => r.status === 'needs_manual_action').length, color: '#3B82F6', bgLight: '#EFF6FF', bgDark: '#1E40AF20' },
            { label: 'Approved', value: requests.filter(r => r.status === 'approved').length, color: '#22C55E', bgLight: '#F0FDF4', bgDark: '#16653420' },
            { label: 'Rejected', value: requests.filter(r => r.status === 'rejected').length, color: '#EF4444', bgLight: '#FFF1F2', bgDark: '#9F123920' },
          ].map(({ label, value, color, bgLight, bgDark }) => (
            <div
              key={label}
              className={`rounded-xl border p-3.5 transition-all ${isDark ? 'border-gray-800' : 'border-gray-200'}`}
              style={{ backgroundColor: isDark ? bgDark : bgLight }}
            >
              <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
              <p className="mt-1 text-2xl font-bold tabular-nums" style={{ color }}>{value}</p>
            </div>
          ))}
        </div>

        {/* ── Content area ─────────────────────────────────────────────── */}
        {loading ? (
          <div className={`rounded-2xl border p-20 flex flex-col items-center justify-center ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <div
              className="animate-spin rounded-full h-10 w-10 border-[3px] border-gray-200 dark:border-gray-700 mb-4"
              style={{ borderTopColor: primaryColor }}
            />
            <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading requests…</p>
          </div>
        ) : error ? (
          <div className={`rounded-2xl border p-16 flex flex-col items-center justify-center text-center ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <div className="w-14 h-14 rounded-2xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v4m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z"/>
              </svg>
            </div>
            <p className={`text-sm font-semibold mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Something went wrong</p>
            <p className={`text-sm mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{error}</p>
            <button
              onClick={fetchRequests}
              className="px-4 py-2 rounded-xl text-sm font-medium text-white transition-colors"
              style={{ backgroundColor: primaryColor }}
            >
              Try again
            </button>
          </div>
        ) : requests.length === 0 ? (
          <div className={`rounded-2xl border p-20 flex flex-col items-center justify-center text-center ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
              style={{ background: `linear-gradient(135deg, ${primaryColor}18, ${primaryColor}08)`, border: `1px solid ${primaryColor}20` }}
            >
              <CalendarIcon size={8} />
            </div>
            <p className={`text-base font-bold mb-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>No reschedule requests</p>
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {activeFilterCount > 0
                ? 'No results match your filters. Try adjusting or clearing them.'
                : 'All caught up — no pending requests right now.'}
            </p>
            {activeFilterCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="mt-4 px-4 py-2 rounded-xl text-sm font-medium text-white transition-colors"
                style={{ backgroundColor: primaryColor }}
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          /* ── Requests cards (responsive layout) ─────────────────────── */
          <div className="space-y-3">
            <div className={`flex flex-col gap-2 rounded-xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${isDark ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'}`}>
              <div>
                <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {totalRequests} request{totalRequests !== 1 ? 's' : ''}
                </p>
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  {statusTab === 'active' ? 'Pending and needs-action requests are ready for review.' : 'Filtered request history for this company.'}
                </p>
              </div>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  <ClearIcon />
                  Clear filters
                </button>
              )}
            </div>
            {requests.map((req) => {
              const badge = STATUS[req.status] || STATUS.pending;
              const canAction = req.status === 'pending' || req.status === 'needs_manual_action';
              const reqDates = req.requested_dates?.length > 0
                ? req.requested_dates
                : req.requested_date ? [req.requested_date] : [];

              return (
                <div
                  key={req.id}
                  className={`group overflow-hidden rounded-2xl border transition-all hover:shadow-md ${
                    isDark ? 'bg-gray-900 border-gray-800 hover:border-gray-700' : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="h-1 w-full" style={{ backgroundColor: badge.dot }} />
                  <div className="p-4 sm:p-5">
                    {/* Top row — Candidate + Status + Actions */}
                    <div className="mb-4 flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                      <div className="flex min-w-0 items-start gap-3">
                        <div
                          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-sm font-bold text-white shadow-sm"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {(req.candidate_name || '?')[0].toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className={`truncate text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {req.candidate_name || '—'}
                            </h3>
                            <span
                              className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border"
                              style={{ backgroundColor: badge.bg, color: badge.color, borderColor: badge.ring }}
                            >
                              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: badge.dot }} />
                              {badge.label}
                            </span>
                          </div>
                          <div className={`mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {req.candidate_email && (
                              <span className="flex min-w-0 items-center gap-1">
                                <MailIcon />
                                <span className="truncate">{req.candidate_email}</span>
                              </span>
                            )}
                            {req.mobile_number && (
                              <span className="flex items-center gap-1">
                                <PhoneIcon />
                                {req.mobile_number}
                              </span>
                            )}
                            {req.created_at && (
                              <span>
                                Requested {new Date(req.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Actions for actionable items OR resolved info for historical */}
                      {canAction ? (
                        <div className="flex flex-shrink-0 items-center gap-2 xl:ml-auto">
                          <button
                            onClick={() => openApprove(req)}
                            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:shadow-md sm:flex-none"
                            style={{ backgroundColor: '#16A34A' }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#15803D'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#16A34A'}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/>
                            </svg>
                            Approve
                          </button>
                          <button
                            onClick={() => { setRejectingReq(req); setDeclineReason(''); }}
                            className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border px-4 py-2 text-xs font-semibold transition-all hover:shadow-sm sm:flex-none ${
                              isDark
                                ? 'bg-red-900/20 text-red-400 border-red-800 hover:bg-red-900/40'
                                : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                            }`}
                          >
                            <ClearIcon />
                            Reject
                          </button>
                        </div>
                      ) : req.status === 'approved' && req.approved_date ? (
                        <div className={`flex items-center gap-2 flex-shrink-0 sm:ml-auto text-xs ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                          <span className="font-medium">
                            Rescheduled to {req.approved_date}{req.approved_time ? ` at ${formatTime12hr(req.approved_time)}` : ''}
                          </span>
                        </div>
                      ) : req.status === 'rejected' ? (
                        <div className={`flex items-center gap-2 flex-shrink-0 sm:ml-auto text-xs ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                          <span className="font-medium">Request declined</span>
                        </div>
                      ) : null}
                    </div>

                    {/* Info grid */}
                    <div className={`grid grid-cols-1 gap-3 border-t pt-4 sm:grid-cols-2 xl:grid-cols-[1.15fr_1fr_1fr_1.4fr] ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                      {/* Job title */}
                      <InfoTile icon={<BriefcaseIcon />} label="Job" isDark={isDark}>
                        <span className="font-medium">{req.job_title || '—'}</span>
                      </InfoTile>

                      {/* Current slot */}
                      <InfoTile icon={<CalendarIcon size={3} />} label="Current slot" isDark={isDark}>
                        {req.original_date ? (
                          <div className="font-medium">
                            <span>{req.original_date}</span>
                            {req.original_time && <span className={`ml-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>at {formatTime12hr(req.original_time)}</span>}
                          </div>
                        ) : (
                          <span className={isDark ? 'text-gray-600' : 'text-gray-300'}>—</span>
                        )}
                      </InfoTile>

                      {/* Preferred dates */}
                      <InfoTile icon={<CalendarIcon size={3} />} label="Preferred dates" isDark={isDark}>
                        <div className="flex flex-wrap gap-1.5">
                          {reqDates.length > 0
                            ? reqDates.map((d) => (
                                <span
                                  key={d}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium whitespace-nowrap"
                                  style={{ backgroundColor: `${primaryColor}12`, color: primaryColor, border: `1px solid ${primaryColor}25` }}
                                >
                                  <CalendarIcon size={3} />
                                  {d}
                                </span>
                              ))
                            : <span className={isDark ? 'text-gray-600' : 'text-gray-300'}>—</span>}
                        </div>
                      </InfoTile>

                      {/* Reason */}
                      <InfoTile icon={<MessageIcon />} label="Reason" isDark={isDark}>
                        <p
                          className="line-clamp-2"
                          title={req.candidate_reason || req.email_content || ''}
                        >
                          {req.candidate_reason || req.email_content || '—'}
                        </p>
                      </InfoTile>
                    </div>

                    {/* Created at timestamp */}
                    {req.updated_at && req.updated_at !== req.created_at && (
                      <div className={`mt-3 flex items-center justify-end border-t pt-3 ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                        <span className={`text-[11px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                          Updated {new Date(req.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Results count footer */}
            <div className={`flex flex-col gap-3 rounded-xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${isDark ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'}`}>
              <p className={`text-xs font-medium ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                Showing {pageStart}-{pageEnd} of {totalRequests} request{totalRequests !== 1 ? 's' : ''}
                {activeFilterCount > 0 && ' (filtered)'}
              </p>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={!canGoPrevious}
                  className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    isDark
                      ? 'border-gray-700 text-gray-300 hover:bg-gray-800 disabled:text-gray-600'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50 disabled:text-gray-300'
                  } disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  Previous
                </button>
                <span className={`min-w-[82px] text-center text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  disabled={!canGoNext}
                  className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    isDark
                      ? 'border-gray-700 text-gray-300 hover:bg-gray-800 disabled:text-gray-600'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50 disabled:text-gray-300'
                  } disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Approve Modal ─────────────────────────────────────────────────── */}
      {approvingReq && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setApprovingReq(null)}>
          <div className={`rounded-2xl shadow-2xl w-full max-w-md overflow-hidden ${isDark ? 'bg-gray-900' : 'bg-white'}`} onClick={(e) => e.stopPropagation()}>
            {/* Modal header */}
            <div className={`px-6 py-5 border-b flex items-center justify-between ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
                <div>
                  <h2 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Approve & Schedule</h2>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{approvingReq.candidate_name}</p>
                </div>
              </div>
              <button
                onClick={() => setApprovingReq(null)}
                className={`p-2 rounded-xl transition-colors ${isDark ? 'text-gray-500 hover:text-gray-300 hover:bg-gray-800' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Select a date and time to book this interview. A confirmation email will be sent to the candidate.
              </p>

              {/* Preferred dates chips */}
              {preferredDates.length > 0 && (
                <div>
                  <p className={`text-[10px] font-semibold uppercase tracking-wider mb-2.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Candidate's preferred dates
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {preferredDates.map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setApproveDate(d)}
                        className={`px-3.5 py-2 rounded-xl text-xs font-semibold border-2 transition-all ${
                          approveDate === d
                            ? 'text-white border-transparent shadow-md'
                            : isDark
                              ? 'bg-gray-800 text-gray-300 border-gray-700 hover:border-green-500/50'
                              : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-green-400'
                        }`}
                        style={approveDate === d ? { backgroundColor: '#16A34A', borderColor: '#16A34A' } : {}}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Date & Time inputs */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-xs font-semibold mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Interview Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={approveDate}
                    min={today}
                    onChange={(e) => setApproveDate(e.target.value)}
                    className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                      isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-semibold mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Interview Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={approveTime}
                    onChange={(e) => setApproveTime(e.target.value)}
                    className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                      isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                    }`}
                  />
                </div>
              </div>

              {approveError && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v4m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z"/>
                  </svg>
                  <p className="text-xs text-red-600 dark:text-red-400">{approveError}</p>
                </div>
              )}
            </div>

            <div className={`px-6 py-4 border-t flex gap-3 justify-end ${isDark ? 'border-gray-800 bg-gray-800/50' : 'border-gray-100 bg-gray-50'}`}>
              <button
                onClick={() => setApprovingReq(null)}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                  isDark ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={approveLoading}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-green-600 hover:bg-green-700 disabled:opacity-60 transition-all shadow-sm hover:shadow-md flex items-center gap-2"
              >
                {approveLoading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                    Scheduling…
                  </>
                ) : 'Confirm & Book'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject Modal ──────────────────────────────────────────────────── */}
      {rejectingReq && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setRejectingReq(null)}>
          <div className={`rounded-2xl shadow-2xl w-full max-w-md overflow-hidden ${isDark ? 'bg-gray-900' : 'bg-white'}`} onClick={(e) => e.stopPropagation()}>
            <div className={`px-6 py-5 border-b flex items-center justify-between ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </div>
                <div>
                  <h2 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Reject Request</h2>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{rejectingReq.candidate_name}</p>
                </div>
              </div>
              <button
                onClick={() => setRejectingReq(null)}
                className={`p-2 rounded-xl transition-colors ${isDark ? 'text-gray-500 hover:text-gray-300 hover:bg-gray-800' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div className={`flex items-start gap-3 p-3.5 rounded-xl ${isDark ? 'bg-amber-900/15 border border-amber-800/40' : 'bg-amber-50 border border-amber-200'}`}>
                <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.268 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                </svg>
                <p className={`text-xs leading-relaxed ${isDark ? 'text-amber-200' : 'text-amber-800'}`}>
                  The candidate will be notified that their request was declined and given a link to rebook from available slots.
                </p>
              </div>

              <div>
                <label className={`block text-xs font-semibold mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Decline Reason <span className={`font-normal ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>(optional)</span>
                </label>
                <textarea
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  placeholder="e.g. Interviewer not available on the requested dates."
                  rows={3}
                  className={`w-full px-3 py-2.5 rounded-xl border text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none transition-all ${
                    isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                  }`}
                />
              </div>
            </div>

            <div className={`px-6 py-4 border-t flex gap-3 justify-end ${isDark ? 'border-gray-800 bg-gray-800/50' : 'border-gray-100 bg-gray-50'}`}>
              <button
                onClick={() => setRejectingReq(null)}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                  isDark ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={rejectLoading}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 transition-all shadow-sm hover:shadow-md flex items-center gap-2"
              >
                {rejectLoading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                    Rejecting…
                  </>
                ) : 'Reject Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RescheduleRequestsPage;
